import { ChevronDown, Loader2 } from 'lucide-react';
import React, { useMemo } from 'react';
import { inputSizes, inputVariants } from 'src/tef/components/config/variantConfig';
import { useResponsiveVariant } from 'src/tef/components/hooks/useResponsiveVariant';
import { useTEF } from 'src/tef/components/providers/TEFProvider';
import { cn } from 'src/tef/components/utils/cn';
import { forwardProps } from 'src/tef/components/utils/forwardProps';
import { getBorderRadiusBySize } from 'src/tef/components/utils/getBorderRadius';
import { containsMaliciousPatterns } from 'src/tef/components/utils/security';
import type { ComponentVariant, ForwardedHTMLProps } from 'src/tef/components/utils/types';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  group?: string;
}

interface SelectDropdownProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>, ForwardedHTMLProps {
  options: SelectOption[];
  variant?: ComponentVariant;
  error?: boolean;
  loading?: boolean;
  placeholder?: string;
  emptyMessage?: string;
  className?: string;
}

export const SelectDropdown = React.memo(
  React.forwardRef<HTMLSelectElement, SelectDropdownProps>(
    (
      {
        options,
        variant = {},
        error = false,
        loading = false,
        placeholder,
        emptyMessage = 'No options available',
        disabled,
        className,
        ...props
      },
      ref
    ) => {
      const tef = useTEF();
      const forwardedProps = forwardProps(props);

      const variantSize = variant.size || 'md';
      const responsiveSize = useResponsiveVariant(variantSize);
      const resolvedSize = typeof responsiveSize === 'string' ? responsiveSize : 'md';
      const sizeConfig = typeof resolvedSize === 'string' ? inputSizes[resolvedSize] : inputSizes.md;

      const borderRadius = getBorderRadiusBySize(resolvedSize, tef);
      const isDisabled = disabled || loading;

      const sanitizedOptions = useMemo((): SelectOption[] => {
        const result: SelectOption[] = [];

        for (const option of options) {
          const label = String(option.label);
          const value = String(option.value);

          if (containsMaliciousPatterns(label) || containsMaliciousPatterns(value)) {
            continue;
          }

          const safeOption: SelectOption = {
            value,
            label,
            disabled: option.disabled,
          };

          if (option.group) {
            if (!containsMaliciousPatterns(option.group)) {
              safeOption.group = option.group;
            }
          }

          result.push(safeOption);
        }

        return result;
      }, [options]);

      const groupedOptions = useMemo(() => {
        const groups: Record<string, SelectOption[]> = {};
        const ungrouped: SelectOption[] = [];

        for (const option of sanitizedOptions) {
          if (option.group) {
            if (!groups[option.group]) {
              groups[option.group] = [];
            }
            groups[option.group].push(option);
          } else {
            ungrouped.push(option);
          }
        }

        return { groups, ungrouped };
      }, [sanitizedOptions]);

      const selectClasses = useMemo(() => {
        return cn(
          'w-full',
          sizeConfig.padding,
          sizeConfig.text,
          borderRadius,
          'border',
          error ? inputVariants.default.error : inputVariants.default.base,
          error ? inputVariants.default.error : inputVariants.default.focus,
          'bg-white dark:bg-slate-800',
          tef.colors.inputText,
          'transition-all duration-200',
          'focus:outline-none',
          'appearance-none',
          'cursor-pointer',
          'pr-10',
          isDisabled && 'opacity-50 cursor-not-allowed',
          loading && 'animate-pulse',
          className
        );
      }, [sizeConfig, error, borderRadius, isDisabled, loading, className, tef.colors.inputText]);

      const hasGroups = Object.keys(groupedOptions.groups).length > 0;

      return (
        <section className="relative">
          <select
            ref={ref}
            disabled={isDisabled}
            aria-invalid={error}
            aria-busy={loading}
            className={selectClasses}
            {...forwardedProps}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}

            {sanitizedOptions.length === 0 && (
              <option value="" disabled>
                {emptyMessage}
              </option>
            )}

            {!hasGroups &&
              groupedOptions.ungrouped.map((option) => (
                <option key={option.value} value={option.value} disabled={option.disabled}>
                  {option.label}
                </option>
              ))}

            {hasGroups && (
              <>
                {groupedOptions.ungrouped.length > 0 &&
                  groupedOptions.ungrouped.map((option) => (
                    <option key={option.value} value={option.value} disabled={option.disabled}>
                      {option.label}
                    </option>
                  ))}

                {Object.entries(groupedOptions.groups).map(([groupName, groupOptions]) => (
                  <optgroup key={groupName} label={groupName}>
                    {groupOptions.map((option) => (
                      <option key={option.value} value={option.value} disabled={option.disabled}>
                        {option.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
              </>
            )}
          </select>

          <span
            className={cn('absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none', tef.colors.muted)}
            aria-hidden="true"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <ChevronDown className="w-5 h-5" />}
          </span>
        </section>
      );
    }
  )
);

SelectDropdown.displayName = 'SelectDropdown';

export default SelectDropdown;
